import { Alerts, Button, Text } from '@components/ui';
import { useDashboardQuery } from '@lib/api/dashboard';
import { useClearNotificationsMutation, useDeleteNotificationMutation, useNotificationsQuery } from '@lib/api/notifications';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { ResponsiveStyleValue } from '@mui/system';
import { useTranslation } from 'react-i18next';

import type { NotificationItem } from './notificationTypes';

type DashboardStats = {
  data?: {
    unSeenNotificationsCount?: number;
  };
};

interface NotificationsCardProps {
  listMaxHeight?: ResponsiveStyleValue<number | string>;
}

export const NotificationsCard = ({
  listMaxHeight = 320,
}: NotificationsCardProps): JSX.Element => {
  const { t } = useTranslation();
  const deleteNotification = useDeleteNotificationMutation();
  const clearNotifications = useClearNotificationsMutation();
  const { data: notificationResult = [] } = useNotificationsQuery();
  const { data: dashboardBody = {} } = useDashboardQuery();
  const notifications = notificationResult as NotificationItem[];
  const unseen =
    (dashboardBody as DashboardStats).data?.unSeenNotificationsCount ?? 0;

  const deleteOne = (id: string | number): void => {
    deleteNotification.mutate(id, {
      onSuccess: () => {
        Alerts.successMessage(t('notifications.deleted'));
      },
    });
  };

  const deleteAll = (): void => {
    clearNotifications.mutate(undefined, {
      onSuccess: () => {
        Alerts.successMessage(t('notifications.cleared'));
      },
    });
  };

  return (
    <Box
      sx={{
        overflow: 'hidden',
        borderRadius: 3,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Text as="h2" variant="subtitle" sx={{ m: 0 }}>
            {t('notifications.title')}
          </Text>
          {unseen > 0 ? (
            <Text variant="caption" tone="muted">
              {t('notifications.unseen', { count: unseen })}
            </Text>
          ) : null}
        </Box>
        {notifications.length > 0 ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => Alerts.altMessage(deleteAll)}
          >
            {t('notifications.clearAll')}
          </Button>
        ) : null}
      </Box>
      {notifications.length > 0 ? (
        <Box sx={{ maxHeight: listMaxHeight, overflowY: 'auto' }}>
          {notifications.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                '&:last-of-type': { borderBottom: 0 },
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Text variant="label" sx={{ display: 'block' }}>
                  {item.title}
                </Text>
                <Text
                  variant="caption"
                  tone="muted"
                  sx={{ display: 'block', mt: 0.25, overflowWrap: 'anywhere' }}
                >
                  {item.body}
                </Text>
              </Box>
              <IconButton
                aria-label={t('notifications.delete')}
                size="small"
                onClick={() =>
                  Alerts.altMessage(() => {
                    deleteOne(item.id);
                  })
                }
                sx={{ color: 'text.secondary' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      ) : (
        <Text
          variant="body"
          tone="muted"
          sx={{ display: 'block', px: 2, py: 3 }}
        >
          {t('notifications.empty')}
        </Text>
      )}
    </Box>
  );
};
